import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { BarChart3 } from 'lucide-react';
import { useConsultantSourceBreakdown } from '@/hooks/useConsultantSourceBreakdown';
import { useTranslation } from 'react-i18next';
import { getSourceLabel } from '@/lib/sourceChannelTypes';

interface Props {
  consultantId: string | null;
  year: number;
  month: number;
}

const formatUSD = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v);

function getProgressColor(pct: number): string {
  if (pct >= 100) return 'bg-green-500';
  if (pct >= 50) return 'bg-blue-500';
  return 'bg-red-500';
}

export default function ConsultantSourceBreakdown({ consultantId, year, month }: Props) {
  const { t } = useTranslation();
  const { data: rows, isLoading } = useConsultantSourceBreakdown(consultantId, year, month);

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-5 w-5 text-primary" />
            Meta por Canal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!rows || rows.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-5 w-5 text-primary" />
          Meta por Canal
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Origem</TableHead>
              <TableHead className="text-right w-16">%</TableHead>
              <TableHead className="text-right">Meta $</TableHead>
              <TableHead className="text-right">Realizado $</TableHead>
              <TableHead className="text-right">Meta Qtd</TableHead>
              <TableHead className="text-right">Realizado Qtd</TableHead>
              <TableHead className="w-[140px]">Progresso</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.source}>
                <TableCell className="font-medium text-sm">{getSourceLabel(row.source, t)}</TableCell>
                <TableCell className="text-right text-sm text-muted-foreground">
                  {row.weightPct !== null ? `${row.weightPct}%` : '—'}
                </TableCell>
                <TableCell className="text-right text-sm">
                  {row.goalAmount !== null ? formatUSD(row.goalAmount) : '—'}
                </TableCell>
                <TableCell className="text-right text-sm font-medium">
                  {formatUSD(row.actualAmount)}
                </TableCell>
                <TableCell className="text-right text-sm">
                  {row.goalDeals !== null ? row.goalDeals : '—'}
                </TableCell>
                <TableCell className="text-right text-sm font-medium">
                  {row.actualCount}
                </TableCell>
                <TableCell>
                  {row.pctAchieved !== null ? (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${getProgressColor(row.pctAchieved)}`}
                          style={{ width: `${Math.min(100, row.pctAchieved)}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground w-10 text-right">
                        {Math.round(row.pctAchieved)}%
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
